import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { evaluationScore, isEvaluationPending, numericScore } from '../../utils/evaluationStatus';
import {
  hasMixedLanguageLeftovers,
  translateStringsProgressively
} from '../../utils/autoTranslate';
import { translateDoctorReviewText } from './doctorReviewTranslator';
import { FiClipboard, FiEdit3, FiFileText, FiLogOut, FiSearch, FiUser } from 'react-icons/fi';
import CaseForm from './CaseForm';
import './DoctorReview.css';

// ── helpers ────────────────────────────────────────────────────────────────

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function ScoreBar({ label, score, max, color }) {
  const { t } = useLanguage();
  const value = numericScore(score);
  const pct = max > 0 && value !== null ? Math.max(0, Math.min(100, Math.round((value / max) * 100))) : 0;
  return (
    <div className="dr-bar-row">
      <div className="dr-bar-header">
        <span className="dr-bar-label">{label}</span>
        <span className="dr-bar-score" style={{ color }}>{value === null ? t('awaitingReview') : `${value}/${max}`}</span>
      </div>
      {value !== null && <div className="dr-bar-bg">
        <div className="dr-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>}
    </div>
  );
}

const asTextList = (value) => {
  if (Array.isArray(value)) return value.map(v => String(v || '').trim()).filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  if (!value) return [];
  return [JSON.stringify(value)];
};

const doctorDashboardText = {
  ru: {
    'Student review': 'Проверка студентов', 'Edit cases': 'Редактировать случаи', 'Create a new case': 'Создать новый случай',
    'Manage reviews and clinical cases from one place.': 'Проверяйте работы и управляйте клиническими случаями в одном месте.',
    'Students are sorted alphabetically; each submission is labelled by case.': 'Студенты отсортированы по алфавиту; каждая работа отмечена названием случая.',
    'Choose a case to update its content and scoring rubric.': 'Выберите случай, чтобы изменить его содержание и критерии оценки.',
    'Open a student and review every submission, organized by case.': 'Откройте студента и проверьте все его работы по случаям.',
    'Update patient details, expected answers, and scoring weights.': 'Изменяйте данные пациента, ожидаемые ответы и веса оценки.',
    'Build a complete clinical scenario and its item-by-item rubric.': 'Создайте полный клинический сценарий и построчные критерии.',
    'Review': 'Проверка', 'students': 'студентов', 'New': 'Новый', 'Back': 'Назад', 'Edit': 'Редактировать', 'case': 'случай', 'Search students': 'Поиск студентов', 'Search by student name or email…': 'Поиск по имени или электронной почте…', 'No students match your search.': 'Студенты не найдены.'
  },
  uz: {
    'Student review': 'Talabalarni tekshirish', 'Edit cases': 'Holatlarni tahrirlash', 'Create a new case': 'Yangi holat yaratish',
    'Manage reviews and clinical cases from one place.': 'Topshiriqlar va klinik holatlarni bir joydan boshqaring.',
    'Students are sorted alphabetically; each submission is labelled by case.': 'Talabalar alifbo tartibida; har bir topshiriq holat nomi bilan ko‘rsatilgan.',
    'Choose a case to update its content and scoring rubric.': 'Mazmuni va baholash mezonlarini o‘zgartirish uchun holatni tanlang.',
    'Open a student and review every submission, organized by case.': 'Talabani ochib, uning holatlar bo‘yicha barcha topshiriqlarini tekshiring.',
    'Update patient details, expected answers, and scoring weights.': 'Bemor ma’lumotlari, kutilgan javoblar va baholash vaznlarini o‘zgartiring.',
    'Build a complete clinical scenario and its item-by-item rubric.': 'To‘liq klinik ssenariy va bandma-band mezon yarating.',
    'Review': 'Tekshirish', 'students': 'talaba', 'New': 'Yangi', 'Back': 'Orqaga', 'Edit': 'Tahrirlash', 'case': 'holat', 'Search students': 'Talabalarni qidirish', 'Search by student name or email…': 'Talaba ismi yoki elektron pochtasi bo‘yicha qidiring…', 'No students match your search.': 'Qidiruvga mos talabalar topilmadi.'
  }
};

function ClinicalValue({ value, language }) {
  if (value === null || value === undefined || value === '') return <span>—</span>;
  if (Array.isArray(value)) {
    return value.length ? <ul className="dr-sub-list">{value.map((item, index) => <li key={index}><ClinicalValue value={item} language={language} /></li>)}</ul> : <span>—</span>;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    return entries.length ? <ul className="dr-sub-list">{entries.map(([key, item]) => <li key={key}><b>{translateDoctorReviewText(key.replace(/_/g, ' '), language)}:</b> <ClinicalValue value={item} language={language} /></li>)}</ul> : <span>—</span>;
  }
  if (typeof value === 'boolean') {
    return <span>{value ? ({ en: 'Yes', ru: 'Да', uz: 'Ha' }[language]) : ({ en: 'No', ru: 'Нет', uz: 'Yo‘q' }[language])}</span>;
  }
  return <span>{translateDoctorReviewText(String(value), language)}</span>;
}

export function PatientCard({ data }) {
  const { language, t } = useLanguage();
  const d = useMemo(() => {
    if (!data || language === 'en') return data || {};
    // Field names are schema keys. Translate values without renaming properties
    // that the card reads (or dropping them in another interface language).
    const translateValues = value => {
      if (typeof value === 'string') return translateDoctorReviewText(value, language);
      if (Array.isArray(value)) return value.map(translateValues);
      if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, nested]) => [key, translateValues(nested)]));
      return value;
    };
    return translateValues(data);
  }, [data, language]);

  const ageGenderMeta = useMemo(() => {
    const age = d.age ? `${d.age} ` : '';
    const ageUnit = language === 'ru' ? 'лет' : language === 'uz' ? 'yosh' : 'y/o';
    const gender = d.gender ? translateDoctorReviewText(d.gender, language) : '';
    return `${age}${ageUnit} ${gender}`.trim();
  }, [d.age, d.gender, language]);

  const meds = Array.isArray(d.medications) ? d.medications : [];
  const pmhRaw = d.past_medical_history;
  const pmh = Array.isArray(pmhRaw) ? pmhRaw : (pmhRaw ? [String(pmhRaw)] : []);
  const allergies = d.allergies || '—';

  return (
    <div className="dr-patient-card">
      <div className="dr-patient-header">
        <div className="dr-patient-avatar">{(d.name || '?')[0]}</div>
        <div>
          <h2 className="dr-patient-name">{d.name || t('notAvailable')}</h2>
          <p className="dr-patient-meta">
            {ageGenderMeta} · <em>{d.chief_complaint}</em>
          </p>
        </div>
      </div>

      <div className="dr-info-grid">
        <Section title={t('presentingHistory')}>
          <p>{d.history_of_presenting_complaint || '—'}</p>
        </Section>

        <Section title={t('pastHistory')}>
          {pmh.length ? <ul>{pmh.map((x, i) => <li key={i}>{translateDoctorReviewText(x, language)}</li>)}</ul> : <p>—</p>}
        </Section>

        <Section title={t('medications')}>
          {meds.length ? (
            <table className="dr-table">
              <thead><tr><th>{t('drug')}</th><th>{t('dose')}</th><th>{t('frequency')}</th></tr></thead>
              <tbody>{meds.map((m, i) => (
                <tr key={i}>
                  <td>{translateDoctorReviewText(m.name, language)}</td>
                  <td>{translateDoctorReviewText(m.dose, language)}</td>
                  <td>{translateDoctorReviewText(m.frequency, language)}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <p>{t('none')}</p>}
        </Section>

        <Section title={t('allergies')}><p>{translateDoctorReviewText(allergies, language)}</p></Section>

        <Section title={t('familyHistory')}>
          <ClinicalValue value={d.family_history} language={language} />
        </Section>

        <Section title={t('socialHistory')}>
          <ClinicalValue value={d.social_history} language={language} />
        </Section>

        <Section title={t('physicalExamination')}>
          <ClinicalValue value={d.physical_examination} language={language} />
        </Section>

        {d.lab_reports && (
          <Section title={t('labReports')}>
            <pre className="dr-pre">{JSON.stringify(d.lab_reports, null, 2)}</pre>
          </Section>
        )}

        {d.imaging && (
          <Section title={t('imaging')}>
            <pre className="dr-pre">{JSON.stringify(d.imaging, null, 2)}</pre>
          </Section>
        )}

        <Section title={t('expectedDiagnosis')} accent>
          <p>{d.final_diagnosis || d.confirmed_diagnosis ||
              (d.mdt_outcome && d.mdt_outcome.diagnosis) || '—'}</p>
        </Section>

        {(d.management_plan || d.mdt_outcome?.initial_management) && (
          <Section title={t('expectedManagement')} accent>
            <ClinicalValue value={d.management_plan ?? d.mdt_outcome?.initial_management} language={language} />
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, accent }) {
  return (
    <div className={`dr-section ${accent ? 'dr-section-accent' : ''}`}>
      <h3 className="dr-section-title">{title}</h3>
      {children}
    </div>
  );
}

// ── Submission detail (transcript + scoring) ───────────────────────────────

export function SubmissionDetail({ sub, onScored }) {
  const { language, t } = useLanguage();
  const [hScore, setHScore] = useState(sub.doctor_score_history ?? '');
  const [dScore, setDScore] = useState(sub.doctor_score_diagnosis ?? '');
  const [mScore, setMScore] = useState(sub.doctor_score_management ?? '');
  const [comment, setComment] = useState(sub.doctor_comment ?? '');
  const [saving, setSaving] = useState(false);
  const [completeTranslationMap, setCompleteTranslationMap] = useState({});
  const [saved, setSaved] = useState(
    sub.doctor_score_history != null
  );

  const transcript = useMemo(() => sub.transcript || [], [sub.transcript]);
  const bd = sub.system_result?.breakdown;
  const scoredChecklist = sub.system_result?.checklist;
  const pendingEvaluation = isEvaluationPending(sub.system_result || {}, sub.system_score);

  const displayDynamicText = useCallback((text) => {
    if (!text || language === 'en') return text;
    const trimmed = String(text).trim();
    const translated = completeTranslationMap[trimmed];
    if (translated && !hasMixedLanguageLeftovers(translated, language)) {
      return translateDoctorReviewText(translated, language);
    }

    const localTranslation = translateDoctorReviewText(trimmed, language);
    if (localTranslation !== trimmed && !hasMixedLanguageLeftovers(localTranslation, language)) {
      return localTranslation;
    }

    return trimmed;
  }, [completeTranslationMap, language]);

  const collectStudentWorkTexts = useCallback(() => {
    const values = [];
    const add = (value) => {
      asTextList(value).forEach((text) => {
        const trimmed = text.trim();
        if (trimmed) values.push(trimmed);
      });
    };

    add(sub.student_diagnosis);
    add(sub.student_plan);
    transcript.forEach((msg) => add(msg.content));
    Object.values(sub.system_result?.breakdown || {}).forEach((section) => {
      add(section.achieved);
      add(section.missed);
      add(section.lost_marks_reason);
      add(section.improvements);
    });

    return [...new Set(values)];
  }, [sub.student_diagnosis, sub.student_plan, sub.system_result, transcript]);

  useEffect(() => {
    let cancelled = false;
    setCompleteTranslationMap({});
    if (language === 'en') return undefined;

    const texts = collectStudentWorkTexts();
    if (!texts.length) {
      return undefined;
    }

    const strictTranslate = (chunk, targetLanguage) => apiService.translateTexts(chunk, targetLanguage, { strict: true });
    translateStringsProgressively(
      texts,
      language,
      strictTranslate,
      (map) => {
        if (!cancelled) setCompleteTranslationMap(map);
      },
      8,
      { retryMixed: true }
    )
      .then((map) => {
        if (!cancelled) {
          setCompleteTranslationMap(map);
        }
      })
      .catch((error) => {
        console.error('Could not complete student work translation:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [collectStudentWorkTexts, language]);

  async function handleSave(e) {
    e.preventDefault();
    if (hScore === '' || dScore === '' || mScore === '') return;
    setSaving(true);
    try {
      await apiService.submitDoctorScore(
        sub.id,
        Number(hScore),
        Number(dScore),
        Number(mScore),
        comment
      );
      setSaved(true);
      onScored(sub.id, Number(hScore), Number(dScore), Number(mScore));
    } catch {
      alert(t('failedSaveScore'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dr-submission-detail">
      {/* System evaluation breakdown */}
      {pendingEvaluation && <div className="dr-card" role="status"><h3 className="dr-card-title">{t('awaitingReview')}</h3><p>{t('reviewPendingMessage')}</p></div>}
      {!pendingEvaluation && bd && (
        <div className="dr-card">
          <div className="dr-card-title-row">
            <h3 className="dr-card-title">{t('aiBreakdown')}</h3>
          </div>
          <ScoreBar label={t('historyTaking')} score={bd.history?.score} max={Number(bd.history?.max) || 30} color="#3b82f6" />
          <ScoreBar label={t('diagnosis')} score={bd.diagnosis?.score} max={Number(bd.diagnosis?.max) || 40} color="#8b5cf6" />
          <ScoreBar label={t('management')} score={bd.management?.score} max={Number(bd.management?.max) || 30} color="#10b981" />

          <div className="dr-checklist-cols">
            {['history', 'diagnosis', 'management'].map(sec => {
              const section = bd[sec];
              if (!section) return null;
              const achieved = asTextList(section.achieved);
              const partial = asTextList(section.partial);
              const missed = asTextList(section.lost_marks_reason).length
                ? asTextList(section.lost_marks_reason)
                : asTextList(section.missed);
              const missedDetails = asTextList(section.lost_marks_reason).length
                ? asTextList(section.missed)
                : [];
              const improvements = asTextList(section.improvements);
              const sectionLabel = {
                history: t('historyTaking'),
                diagnosis: t('diagnosis'),
                management: t('management')
              }[sec];
              return (
                <div key={sec} className="dr-checklist-col">
                  <h4 className="dr-cl-title">{sectionLabel}</h4>
                  {achieved.map((item, i) => (
                    <p key={i} className="dr-cl-item done">✓ {displayDynamicText(item)}</p>
                  ))}
                  {partial.map((item, i) => (
                    <p key={`partial-${i}`} className="dr-cl-item partial">◐ {displayDynamicText(item)}</p>
                  ))}
                  {missed.map((item, i) => (
                    <p key={i} className="dr-cl-item miss">✗ {displayDynamicText(item)}</p>
                  ))}
                  {missedDetails.map((item, i) => (
                    <p key={i} className="dr-cl-item miss">• {displayDynamicText(item)}</p>
                  ))}
                  {improvements.map((item, i) => (
                    <p key={i} className="dr-cl-item">→ {displayDynamicText(item)}</p>
                  ))}
                </div>
              );
            })}
          </div>
          {scoredChecklist && <div className="dr-item-scores">
            <h4>Item-by-item scoring</h4>
            {[
              ['history_items', t('historyTaking')],
              ['diagnosis_items', t('diagnosis')],
              ['management_items', t('management')]
            ].map(([key, label]) => Array.isArray(scoredChecklist[key]) && scoredChecklist[key].length > 0 && <div className="dr-item-score-section" key={key}>
              <h5>{label}</h5>
              {scoredChecklist[key].map((item, index) => <div className="dr-item-score-row" key={`${key}-${index}`}>
                <span>{displayDynamicText(item.item)}</span>
                <small>Weight {item.weight ?? 1}</small>
                <strong>{item.points_awarded ?? '—'} / {item.max_points ?? '—'}</strong>
              </div>)}
            </div>)}
          </div>}
        </div>
      )}

      {/* Student's written answers */}
      <div className="dr-card">
        <h3 className="dr-card-title">{t('studentDiagnosis')}</h3>
        <p className="dr-written-text">{displayDynamicText(sub.student_diagnosis) || '—'}</p>
      </div>
      <div className="dr-card">
        <h3 className="dr-card-title">{t('studentPlan')}</h3>
        <p className="dr-written-text">{displayDynamicText(sub.student_plan) || '—'}</p>
      </div>

      {/* Transcript */}
      <div className="dr-card">
        <h3 className="dr-card-title">{t('conversationTranscript')}</h3>
        <div className="dr-transcript">
          {transcript.length === 0 ? (
            <p className="dr-empty">{t('noTranscriptAvailable')}</p>
          ) : transcript.map((msg, i) => (
            <div key={i} className={`dr-msg dr-msg-${msg.role}`}>
              <span className="dr-msg-role">{msg.role === 'user' ? t('student') : t('patient')}</span>
              <p className="dr-msg-text">{displayDynamicText(msg.content)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Doctor scoring form */}
      <div className="dr-card dr-scoring-card">
        <h3 className="dr-card-title">
          {saved ? t('submittedScore') : t('submitScore')}
        </h3>
        {saved && sub.doctor_score_history != null && (
          <div className="dr-saved-scores">
            <ScoreBar label={t('historyTaking')} score={Number(hScore)} max={30} color="#3b82f6" />
            <ScoreBar label={t('diagnosis')} score={Number(dScore)} max={40} color="#8b5cf6" />
            <ScoreBar label={t('management')} score={Number(mScore)} max={30} color="#10b981" />
            {comment && <p className="dr-saved-comment">{t('notesComments')}: {displayDynamicText(comment)}</p>}
          </div>
        )}
        {!saved && (
          <form onSubmit={handleSave} className="dr-score-form">
            <div className="dr-score-fields">
              <label className="dr-score-field">
                <span>{t('historyTaking')} <small>(0-30)</small></span>
                <input type="number" min="0" max="30" value={hScore}
                  onChange={e => setHScore(e.target.value)} required />
              </label>
              <label className="dr-score-field">
                <span>{t('diagnosis')} <small>(0-40)</small></span>
                <input type="number" min="0" max="40" value={dScore}
                  onChange={e => setDScore(e.target.value)} required />
              </label>
              <label className="dr-score-field">
                <span>{t('management')} <small>(0-30)</small></span>
                <input type="number" min="0" max="30" value={mScore}
                  onChange={e => setMScore(e.target.value)} required />
              </label>
            </div>
            <label className="dr-score-comment">
              <span>{t('notesComments')}</span>
              <textarea value={comment} onChange={e => setComment(e.target.value)}
                placeholder={t('optionalFeedback')} rows={3} />
            </label>
            <button type="submit" className="dr-submit-btn" disabled={saving}>
              {saving ? t('saving') : t('submitScore')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function DoctorReview() {
  const { user, logout } = useAuth();
  const { language, t } = useLanguage();
  const f = useCallback(text => doctorDashboardText[language]?.[text] || text, [language]);
  const navigate = useNavigate();
  const [view, setView] = useState('home');
  const [cases, setCases] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [caseDetail, setCaseDetail] = useState(null);
  const [editingCase, setEditingCase] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [studentSearch, setStudentSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user && user.role !== 'doctor') navigate('/');
  }, [user, navigate]);

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [nextCases, nextSubmissions] = await Promise.all([
        apiService.getDoctorCases(),
        apiService.getSubmissions()
      ]);
      setCases(Array.isArray(nextCases) ? nextCases : []);
      setSubmissions(Array.isArray(nextSubmissions) ? nextSubmissions : []);
    } catch {
      setError(t('evaluationError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  const caseById = useMemo(() => Object.fromEntries(cases.map(item => [String(item.id), item])), [cases]);
  const studentGroups = useMemo(() => {
    const groups = new Map();
    submissions.forEach(sub => {
      const key = sub.user_id || sub.student_email || sub.student_name || 'anonymous';
      if (!groups.has(key)) groups.set(key, { id: key, name: sub.student_name || t('anonymous'), email: sub.student_email || '', submissions: [] });
      groups.get(key).submissions.push(sub);
    });
    return [...groups.values()].map(group => ({
      ...group,
      submissions: group.submissions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [submissions, t]);
  const visibleStudentGroups = useMemo(() => {
    const query = studentSearch.trim().toLocaleLowerCase();
    if (!query) return studentGroups;
    return studentGroups.filter(group => `${group.name} ${group.email}`.toLocaleLowerCase().includes(query));
  }, [studentGroups, studentSearch]);

  async function openStudentSubmission(sub) {
    setLoading(true);
    setError('');
    setSubmission(sub);
    try {
      const fullSubmission = await apiService.getSubmission(sub.id);
      const snapshot = fullSubmission.case_snapshot;
      const detail = snapshot ? { case: {
        ...(snapshot.raw_data || {}),
        id: snapshot.id,
        case_number: snapshot.case_number,
        name: snapshot.name,
        age: snapshot.age,
        chief_complaint: snapshot.chief_complaint
      } } : await apiService.getCaseDetail(fullSubmission.case_id || sub.case_id);
      setSubmission(fullSubmission);
      setCaseDetail(detail);
      setView('submission');
    } catch {
      setError(t('evaluationError'));
    } finally {
      setLoading(false);
    }
  }

  async function openCaseEditor(caseSummary) {
    setLoading(true);
    setError('');
    try {
      const detail = await apiService.getCaseDetail(caseSummary.id);
      setEditingCase(detail.case);
      setView('edit-case');
    } catch {
      setError(t('evaluationError'));
    } finally {
      setLoading(false);
    }
  }

  async function finishCaseChange(destination = 'edit-cases') {
    await loadWorkspace();
    setEditingCase(null);
    setView(destination);
  }

  function handleScored(subId, h, d, m) {
    const scores = { doctor_score_history: h, doctor_score_diagnosis: d, doctor_score_management: m };
    setSubmission(prev => prev ? { ...prev, ...scores } : prev);
    setSubmissions(prev => prev.map(item => item.id === subId ? { ...item, ...scores } : item));
  }

  const totalSubmissions = submissions.length;
  const totalReviewed = submissions.filter(item => item.doctor_score_history != null).length;
  const title = view === 'home' ? t('doctorReview')
    : view === 'review' ? f('Student review')
    : view === 'edit-cases' ? f('Edit cases')
    : view === 'edit-case' ? `${f('Edit')} ${editingCase?.name || f('case')}`
    : view === 'create-case' ? f('Create a new case')
    : `${submission?.student_name || t('anonymous')} - ${t('studentSubmission')}`;
  const subtitle = view === 'home' ? f('Manage reviews and clinical cases from one place.')
    : view === 'review' ? f('Students are sorted alphabetically; each submission is labelled by case.')
    : view === 'edit-cases' ? f('Choose a case to update its content and scoring rubric.')
    : view === 'submission' ? `${caseDetail?.case?.name || caseById[submission?.case_id]?.name || ''} · ${fmt(submission?.created_at)}`
    : t('fillScenarioForm');

  const goBack = () => {
    if (view === 'submission') setView('review');
    else if (view === 'edit-case') { setEditingCase(null); setView('edit-cases'); }
    else setView('home');
  };
  const handleLogout = () => {
    if (window.confirm(t('logoutConfirm'))) { logout(); navigate('/login'); }
  };
  return (
    <div className="dr-page">
      <div className="dr-topbar">
        <div className="dr-topbar-left">
          {view !== 'home' && <button type="button" className="dr-back-btn" onClick={goBack}>← {f('Back')}</button>}
          <div><h1 className="dr-title">{title}</h1><p className="dr-subtitle">{subtitle}</p></div>
        </div>
        <div className="dr-topbar-tools">
          {view === 'submission' && submission && <div className="dr-top-score">{isEvaluationPending(submission.system_result || {}, submission.system_score) ? <span>{t('awaitingReview')}</span> : <><span className="dr-score-num">{evaluationScore(submission.system_result || {}, submission.system_score)}</span><span className="dr-score-label">/ 100 {t('aiScore')}</span></>}</div>}
          <div className="dr-account-actions">
            <button type="button" className="dr-account-btn" onClick={() => navigate('/profile')}><FiUser /> {t('profile')}</button>
            <button type="button" className="dr-account-btn dr-signout-btn" onClick={handleLogout}><FiLogOut /> {t('signOut')}</button>
          </div>
        </div>
      </div>

      {loading && <p className="dr-loading">{t('loading')}</p>}
      {error && <p className="dr-error">{error}</p>}

      {!loading && !error && view === 'home' && <>
        <div className="dr-stats dr-home-stats">
          <div className="dr-stat"><span className="dr-stat-num">{cases.length}</span><span className="dr-stat-label">{t('cases')}</span></div>
          <div className="dr-stat"><span className="dr-stat-num">{totalSubmissions}</span><span className="dr-stat-label">{t('submissions')}</span></div>
          <div className="dr-stat"><span className="dr-stat-num">{totalSubmissions - totalReviewed}</span><span className="dr-stat-label">{t('pending')}</span></div>
        </div>
        <div className="dr-home-sections">
          <button type="button" className="dr-home-section review" onClick={() => setView('review')}>
            <span className="dr-home-thumb review-thumb">
              <FiClipboard />
              <span className="thumb-stack thumb-stack-left">
                <i />
                <i />
                <i />
              </span>
              <span className="thumb-score-ring">
                <em>{totalSubmissions - totalReviewed}</em>
                <small>{t('pending')}</small>
              </span>
            </span>
            <span><strong>{f('Review')}</strong><small>{f('Open a student and review every submission, organized by case.')}</small></span>
            <b>{studentGroups.length} {f('students')}</b>
          </button>
          <button type="button" className="dr-home-section edit" onClick={() => setView('edit-cases')}>
            <span className="dr-home-thumb edit-thumb">
              <FiEdit3 />
              <span className="thumb-form-lines">
                <i />
                <i />
                <i />
                <i />
              </span>
              <span className="thumb-weight-pill">{cases.length} {t('cases')}</span>
            </span>
            <span><strong>{f('Edit cases')}</strong><small>{f('Update patient details, expected answers, and scoring weights.')}</small></span>
            <b>{cases.length} {t('cases')}</b>
          </button>
          <button type="button" className="dr-home-section create" onClick={() => setView('create-case')}>
            <span className="dr-home-thumb create-thumb">
              <FiFileText />
              <span className="thumb-plus">+</span>
              <span className="thumb-draft-lines">
                <i />
                <i />
                <i />
              </span>
            </span>
            <span><strong>{f('Create a new case')}</strong><small>{f('Build a complete clinical scenario and its item-by-item rubric.')}</small></span>
            <b>{f('New')}</b>
          </button>
        </div>
      </>}

      {!loading && !error && view === 'review' && <div className="dr-review-workspace">
        <label className="dr-student-search"><span>{f('Search students')}</span><div><FiSearch /><input type="search" value={studentSearch} onChange={event => setStudentSearch(event.target.value)} placeholder={f('Search by student name or email…')} /></div></label>
        <div className="dr-student-groups">
        {studentGroups.length === 0 && <p className="dr-empty">{t('noSubmissions')}</p>}
        {studentGroups.length > 0 && visibleStudentGroups.length === 0 && <p className="dr-empty">{f('No students match your search.')}</p>}
        {visibleStudentGroups.map(group => <section className="dr-student-group" key={group.id}>
          <div className="dr-student-group-head"><div className="dr-case-avatar">{group.name[0] || '?'}</div><div><h2>{group.name}</h2><p>{group.email}</p></div><span>{group.submissions.length} {t('submissions')}</span></div>
          <div className="dr-student-case-list">{group.submissions.map(sub => {
            const caseItem = caseById[String(sub.case_id)] || {};
            return <button type="button" className="dr-student-case" key={sub.id} onClick={() => openStudentSubmission(sub)}>
              <span><strong>{caseItem.name || sub.case_id}</strong><small>{caseItem.chief_complaint || fmt(sub.created_at)}</small></span>
              <span className="dr-student-meta"><small>{fmt(sub.created_at)}</small><span className={`dr-badge ${sub.doctor_score_history != null ? 'badge-done' : 'badge-pending'}`}>{sub.doctor_score_history != null ? t('reviewed') : t('pending')}</span><b>{isEvaluationPending(sub.system_result || {}, sub.system_score) ? t('awaitingReview') : `${evaluationScore(sub.system_result || {}, sub.system_score)}/100`}</b></span>
            </button>;
          })}</div>
        </section>)}
        </div>
      </div>}

      {!loading && !error && view === 'edit-cases' && <div className="dr-case-grid">
        {cases.length === 0 && <p className="dr-empty">{t('noCases')}</p>}
        {cases.map(item => <button type="button" key={item.id} className="dr-case-card dr-edit-case-card" onClick={() => openCaseEditor(item)}>
          <div className="dr-case-avatar">{(item.name || '?')[0]}</div><div className="dr-case-body"><h3 className="dr-case-name">{item.name}</h3><p className="dr-case-complaint">{item.chief_complaint}</p></div><FiEdit3 />
        </button>)}
      </div>}

      {!loading && !error && view === 'create-case' && <CaseForm onCancel={() => setView('home')} onCreated={() => finishCaseChange('home')} />}
      {!loading && !error && view === 'edit-case' && editingCase && <CaseForm caseData={editingCase} onCancel={goBack} onSaved={() => finishCaseChange('edit-cases')} />}
      {!loading && !error && view === 'submission' && submission && <>
        {caseDetail?.case && <PatientCard data={caseDetail.case} />}
        <SubmissionDetail sub={submission} onScored={handleScored} />
      </>}
    </div>
  );
}
